import { Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BehaviorSubject, distinctUntilChanged, map, Observable } from 'rxjs';
import { ApiService } from './api/api.service';
import { LoggingService } from './logging.service';
import { NavigationService } from './navigation.service';
import { BaseModel } from '../types/generated.types';
import { AuthService } from './auth.service';
import { EventBusService, EventType } from './event-bus.service';
import { TenantConfigurationUi } from '../types/tenant.type';
import { TextHelper } from '../helpers/text.helper';
import { MapHelper } from '../helpers/map.helper';
import { Platform } from '../enums/generated.enums';
import { Tenants } from '../enums/page-type.enum';

@Injectable({ providedIn: 'root' })
export class TenantsService {
  private readonly apiService = inject(ApiService);
  private readonly loggingService = inject(LoggingService);
  private readonly navigationService = inject(NavigationService);
  private readonly authService = inject(AuthService);
  private readonly eventBusService = inject(EventBusService);


  private tenantsSubject = new BehaviorSubject<BaseModel[]>([]);
  private selectedTenantSubject = new BehaviorSubject<BaseModel | null>(null);
  private tenantConfigurationsSubject = new BehaviorSubject<TenantConfigurationUi[]>([]);
  private tenantsReadyPromise: Promise<void> | null = null;
  private loadSequence = 0;

  private tenantsById = new Map<string, BaseModel>();
  private tenantConfigurationsById = new Map<string, TenantConfigurationUi>();
  private tenantUrlNamesById = new Map<string, string>();
  private tenantIdsByUrlName = new Map<string, string>();

  get tenants(): BaseModel[] {
    return this.tenantsSubject.value;
  }

  get selectedTenant(): BaseModel {
    return this.selectedTenantSubject.value;
  }

  get selectedTenantId(): string | null {
    return this.selectedTenantSubject.value?.id ?? null;
  }

  get tenantConfigurations(): TenantConfigurationUi[] {
    return this.tenantConfigurationsSubject.value;
  }

  get isAllTenantsSelected(): boolean {
    return this.tenants?.length > 1 && this.selectedTenant?.id == null;
  }

  get selectedTenantPlatform(): Platform | null {
    return this.getTenantPlatform(this.selectedTenantId);
  }


  get tenants$(): Observable<BaseModel[]> {
    return this.tenantsSubject.asObservable();
  }

  get selectedTenant$(): Observable<BaseModel | null> {
    return this.selectedTenantSubject.asObservable();
  }

  get selectedTenantId$(): Observable<string | null> {
    return this.selectedTenantSubject.pipe(
      map(tenant => tenant?.id ?? null),
      distinctUntilChanged(),
    );
  }

  get tenantConfigurations$(): Observable<TenantConfigurationUi[]> {
    return this.tenantConfigurationsSubject.asObservable();
  }

  get hasBluePrismTenant(): boolean {
    return this.tenantConfigurations.some(t => t.platform === Platform.BluePrism || t.platform === Platform.BluePrismNextGen);
  }

  get hasUiPathTenant(): boolean {
    return this.tenantConfigurations.some(t => t.platform === Platform.UiPath);
  }

  get hasAutomationAnywhereTenant(): boolean {
    return this.tenantConfigurations.some(t => t.platform === Platform.AutomationAnywhere);
  }

  get hasPowerAutomateTenant(): boolean {
    return this.tenantConfigurations.some(t => t.platform === Platform.PowerAutomateCloud || t.platform === Platform.PowerAutomateDesktop);
  }

  get hasPowerAutomateDesktopTenant(): boolean {
    return this.tenantConfigurations.some(t => t.platform === Platform.PowerAutomateDesktop);
  }

  get hasPowerAutomateCloudTenant(): boolean {
    return this.tenantConfigurations.some(t => t.platform === Platform.PowerAutomateCloud);
  }

  // Utilization (Machines + Licenses) is only meaningful for non-PA Cloud tenants.
  get showUtilization(): boolean {
    return this.tenantConfigurations.some(t => t.platform !== Platform.PowerAutomateCloud) && !this.isPowerAutomateCloud(this.selectedTenantId);
  }

  constructor() {
    if (this.autoInitializeOnOrganizationReady) {
      this.eventBusService.on(EventType.OrganizationReady)
        .pipe(takeUntilDestroyed())
        .subscribe(() => {
          this.tenantsReadyPromise = this.loadAndNotify();
        });
    }
  }

  // Overridden to false by DumpViewerTenantsService so the dump-viewer instance stays guard-driven.
  protected get autoInitializeOnOrganizationReady(): boolean {
    return true;
  }

  initializeTenants(): Promise<void> {
    this.tenantsReadyPromise = this.loadTenants().then((): void => { /* discard winner flag */ });
    return this.tenantsReadyPromise;
  }

  private async loadAndNotify(): Promise<void> {
    // Only the winning load announces readiness. emit() is synchronous, so SchedulerCacheService's
    // TenantsLoaded handler sets its ready-promise before this method's promise resolves — which
    // TenantGuard relies on when it awaits tenants' then scheduler's whenReady() in sequence.
    if (await this.loadTenants()) {
      this.eventBusService.emit(EventType.TenantsLoaded);
    }
  }

  whenReady(): Promise<void> {
    if (this.tenantsReadyPromise === null) {
      console.warn('TenantsService.whenReady() called before initializeTenants()');
      return Promise.resolve();
    }
    return this.tenantsReadyPromise;
  }

  // Returns true if this load was the most recent one (and therefore committed its result).
  // A stale load (superseded by a newer one while awaiting HTTP) drops its writes so it cannot
  // overwrite the latest org's data — e.g. when deep-linking to an org other than the token org.
  private async loadTenants(): Promise<boolean> {
    const seq = ++this.loadSequence;
    try {
      const tenantsList = await this.apiService.tenants.getTenantsList();
      const configurations = await this.apiService.tenants.getTenantsConfigurationUi();
      if (seq !== this.loadSequence) {
        return false;
      }

      const filteredTenants = tenantsList.filter(t => !t.isDeleted);
      this.tenantsById = MapHelper.toDictionary(tenantsList, t => t.id);
      this.tenantConfigurationsById = MapHelper.toDictionary(configurations, t => t.tenantId);

      const tenantUrlNames = filteredTenants?.map(t => ({
        id: t.id,
        urlName: TextHelper.getUrlNameFromLabel(t.label),
      })) ?? [];
      this.tenantUrlNamesById = MapHelper.toDictionary2(tenantUrlNames, i => i.id, i => i.urlName);
      this.tenantIdsByUrlName = MapHelper.toDictionary2(tenantUrlNames, i => i.urlName, i => i.id);

      this.tenantsSubject.next(filteredTenants);
      this.tenantConfigurationsSubject.next(configurations);
      return true;
    } catch (error: any) {
      this.loggingService.logException(error);
      if (seq !== this.loadSequence) {
        return false;
      }
      this.tenantsSubject.next([]);
      this.tenantConfigurationsSubject.next([]);
      return true;
    }
  }

  selectTenantByUrlName(tenantUrlName: string | null): void {
    const tenantId = this.tenantIdsByUrlName.get(tenantUrlName);
    if (tenantId === this.selectedTenant?.id) {
      return;
    }

    this.selectTenantById(tenantId);
  }

  async selectTenant(tenantId: string): Promise<void> {
    const isSpecial = tenantId === Tenants.AllTenants;
    this.selectTenantById(isSpecial ? null : tenantId);
    const tenantUrlName = isSpecial ? tenantId : this.tenantUrlNamesById.get(tenantId);
    await this.navigationService.navigateToOtherTenant(tenantUrlName);
  }

  getTenantLabel(tenantId: string): string {
    if (!tenantId) return '';
    const tenant = this.tenantsById.get(tenantId);
    return tenant?.label ?? tenantId ?? 'Unknown tenant\'}';
  }

  getTenantConfiguration(tenantId: string): TenantConfigurationUi | null {
    if (!tenantId) return null;
    return this.tenantConfigurationsById.get(tenantId) ?? null;
  }

  getTenantPlatform(tenantId: string): Platform | null {
    if (!tenantId) return null;
    const tenant = this.tenantConfigurationsById.get(tenantId);
    return tenant?.platform ?? null;
  }

  isBluePrism(tenantId: string): boolean {
    return this.getTenantPlatform(tenantId) === Platform.BluePrism;
  }

  isBluePrismNextGen(tenantId: string): boolean {
    return this.getTenantPlatform(tenantId) === Platform.BluePrismNextGen;
  }

  isAnyBluePrism(tenantId: string): boolean {
    const platform = this.getTenantPlatform(tenantId);
    return platform === Platform.BluePrism || platform === Platform.BluePrismNextGen;
  }

  isUiPath(tenantId: string): boolean {
    return this.getTenantPlatform(tenantId) === Platform.UiPath;
  }

  isPowerAutomate(tenantId: string): boolean {
    const platform = this.getTenantPlatform(tenantId);
    return platform === Platform.PowerAutomateCloud || platform === Platform.PowerAutomateDesktop;
  }

  isPowerAutomateDesktop(tenantId: string): boolean {
    return this.getTenantPlatform(tenantId) === Platform.PowerAutomateDesktop;
  }

  isPowerAutomateCloud(tenantId: string): boolean {
    return this.getTenantPlatform(tenantId) === Platform.PowerAutomateCloud;
  }

  private selectTenantById(tenantId: string): void {
    const tenant = this.tenantsById.get(tenantId);
    this.selectedTenantSubject.next(tenant);
  }
}
